import { useState } from 'react';
import { ListChecks, Loader2, Mail, Pencil, Plus, Trash2, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminPage, EmptyState, PageHeader, Panel, SettingRow, adminButton, useAdminPageTitle } from '@/components/admin/ui';
import { ChecklistTemplateDialog } from '@/components/admin/checklists/ChecklistTemplateDialog';
import { PhotoReportsUpgrade } from '@/components/admin/jobs/PhotoReportsUpgrade';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useAuth } from '@/contexts/AuthContext';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useToast } from '@/hooks/use-toast';
import { useOrgPlanLimits } from '@/hooks/useOrgPlanLimits';
import {
  useChecklistTemplates,
  useDeleteChecklistTemplate,
  useSaveChecklistTemplate,
  useServicesByChecklistTemplate,
  type ChecklistTemplate,
  type TemplateDraft,
} from '@/hooks/useChecklists';
import { useJobReportSettings, useSaveJobReportSettings, type JobReportSettings } from '@/hooks/useJobPhotos';
import { STARTER_CHECKLISTS, canUsePhotoReports, groupByRoom } from '@/lib/checklists';

const toDraft = (t: ChecklistTemplate): TemplateDraft => ({
  id: t.id,
  name: t.name,
  description: t.description,
  items: t.items.map((i) => ({ id: i.id, label: i.label, room: i.room })),
});

const ChecklistsAdmin = () => {
  useAdminPageTitle('Checklists');
  const { orgId, loading: orgLoading, canWrite } = useAdminOrg();
  const { staffRole } = useAuth();
  const { assertCanWrite } = useReadOnlyGuard();
  const { toast } = useToast();
  const { data: templates = [], isLoading } = useChecklistTemplates(orgId);
  const { data: servicesByTemplate = {} } = useServicesByChecklistTemplate(orgId);
  const save = useSaveChecklistTemplate(orgId);
  const del = useDeleteChecklistTemplate(orgId);
  const { data: limits } = useOrgPlanLimits(orgId);
  const { data: reportSettings } = useJobReportSettings(orgId);
  const saveSettings = useSaveJobReportSettings(orgId);

  const [editing, setEditing] = useState<TemplateDraft | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState<ChecklistTemplate | null>(null);

  const photosAllowed = limits ? canUsePhotoReports(limits.effectivePlan) : true;

  const openEditor = (draft: TemplateDraft | null) => {
    if (!assertCanWrite()) return;
    setEditing(draft);
    setDialogOpen(true);
  };

  const onSave = (draft: TemplateDraft) =>
    save.mutate(draft, {
      onSuccess: () => {
        setDialogOpen(false);
        toast({ title: draft.id ? 'Checklist saved' : 'Checklist created' });
      },
      onError: (error) =>
        toast({ title: 'Couldn’t save the checklist', description: (error as Error)?.message, variant: 'destructive' }),
    });

  const onDelete = () => {
    if (!toDelete) return;
    del.mutate(toDelete.id, {
      onSuccess: () => toast({ title: 'Checklist deleted', description: 'Jobs that already have it keep their copy.' }),
      onError: (error) =>
        toast({ title: 'Couldn’t delete the checklist', description: (error as Error)?.message, variant: 'destructive' }),
      onSettled: () => setToDelete(null),
    });
  };

  const updateSetting = (patch: Partial<JobReportSettings>) => {
    if (!assertCanWrite()) return;
    const next = { auto_send_on_complete: false, include_photos: true, ...reportSettings, ...patch };
    saveSettings.mutate(next, {
      onError: (error) =>
        toast({ title: 'Couldn’t save that setting', description: (error as Error)?.message, variant: 'destructive' }),
    });
  };

  if (orgLoading || isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const newButton = (
    <Button size="sm" className={adminButton.primary} onClick={() => openEditor(null)} disabled={!canWrite}>
      <Plus />
      New checklist
    </Button>
  );

  return (
    <AdminPage width="narrow">
      <PageHeader
        description="Checklists your cleaners tick off on their phone. Link one to a service and every new job for it, repeat cleans included, gets its own copy."
        actions={newButton}
      />

      <Panel title="Your checklists" icon={<ListChecks />}>
        {templates.length === 0 ? (
          <EmptyState
            compact
            icon={<ListChecks />}
            title="No checklists yet"
            description="Start from one of ours and make it your own, or build one from scratch."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                {STARTER_CHECKLISTS.map((starter) => (
                  <Button
                    key={starter.name}
                    size="sm"
                    variant="outline"
                    className={adminButton.outline}
                    disabled={!canWrite}
                    onClick={() => openEditor({ name: starter.name, description: starter.description, items: starter.items })}
                  >
                    Start from “{starter.name}”
                  </Button>
                ))}
              </div>
            }
          />
        ) : (
          <ul className="adm-divide -my-3.5" data-testid="checklist-templates">
            {templates.map((t) => {
              const rooms = groupByRoom(t.items).map((g) => g.room).filter(Boolean);
              const services = servicesByTemplate[t.id] ?? [];
              return (
                <li key={t.id} className="flex flex-col gap-3 py-3.5 sm:flex-row sm:items-center" data-testid={`checklist-template-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-semibold text-foreground">{t.name}</p>
                    <p className="text-[12.5px] text-muted-foreground">
                      {t.items.length} {t.items.length === 1 ? 'item' : 'items'}
                      {rooms.length > 0 ? ` · ${rooms.join(', ')}` : ''}
                    </p>
                    <p className="text-[12px] text-muted-foreground">
                      {services.length > 0 ? `Used for ${services.join(', ')}` : 'Not linked to a service yet'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className={adminButton.outline} onClick={() => openEditor(toDraft(t))} disabled={!canWrite}>
                      <Pencil /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className={adminButton.danger}
                      aria-label={`Delete ${t.name}`}
                      onClick={() => assertCanWrite() && setToDelete(t)}
                      disabled={!canWrite}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel
        title="Completion reports"
        icon={<Mail />}
        description="When a job’s done, send the customer a short report with the ticked checklist and before and after photos."
      >
        {!photosAllowed ? (
          <PhotoReportsUpgrade canUpgrade={staffRole === 'owner'} />
        ) : (
          <div className="adm-divide -my-3.5">
            <SettingRow
              icon={<Mail />}
              htmlFor="auto-send-report"
              title="Send automatically when a job is completed"
              description="Otherwise you can send it yourself from the job."
              control={
                <Switch
                  id="auto-send-report"
                  checked={Boolean(reportSettings?.auto_send_on_complete)}
                  onCheckedChange={(checked) => updateSetting({ auto_send_on_complete: checked })}
                  disabled={!canWrite || saveSettings.isPending}
                />
              }
            />
            <SettingRow
              icon={<Camera />}
              htmlFor="report-photos"
              title="Include photos"
              description="Photos are shared through private links that expire after 7 days."
              control={
                <Switch
                  id="report-photos"
                  checked={reportSettings?.include_photos ?? true}
                  onCheckedChange={(checked) => updateSetting({ include_photos: checked })}
                  disabled={!canWrite || saveSettings.isPending}
                />
              }
            />
          </div>
        )}
      </Panel>

      <ChecklistTemplateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        saving={save.isPending}
        onSave={onSave}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{toDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Services using it will stop adding it to new jobs. Jobs that already have this checklist keep their copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>Delete checklist</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPage>
  );
};

export default ChecklistsAdmin;
